// ─────────────────────────────────────────────────────────────────────────────
// auth.js
//
// Magic-link (passwordless) authentication flow.
//
// User flow:
//   1. User clicks "Sign In", enters their email in the modal.
//   2. We call sendMagicLink(email). Firebase emails them a one-time link.
//   3. User clicks the link in their email. Their browser opens the app at
//      the link's URL (e.g. https://fractal-music.web.app/?apiKey=...).
//   4. On app load, completeMagicLinkSignInIfPresent() detects the link
//      params and completes the sign-in.
//   5. Firebase Auth fires onAuthStateChanged → userTier.js handles the rest.
//
// The link contains a one-time code in the URL. We also need the email
// address that was used to request it, which we store in localStorage when
// the user first enters it. If the user opens the link on a different
// device, we prompt for the email again at completion time.
// ─────────────────────────────────────────────────────────────────────────────

import {
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  signOut as firebaseSignOut,
} from "firebase/auth";
import { auth } from "./firebase";

const EMAIL_STORAGE_KEY = "fractalToolkit.emailForSignIn";

/**
 * Send a magic sign-in link to the given email address.
 *
 * The link will return the user to whatever URL is in window.location at
 * the time of the request. We strip the search/hash from that URL so the
 * link lands on a clean app URL.
 */
export async function sendMagicLink(email) {
  const isDev = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
  let continueUrl;
  if (isDev) {
    // Local development: use whatever URL we're on
    const url = new URL(window.location.href);
    url.search = "";
    url.hash = "";
    continueUrl = url.toString();
  } else {
    // Production: always use the deployed app URL
    continueUrl = "https://fractal-music.web.app/";
  }

  const actionCodeSettings = {
    url: continueUrl,
    handleCodeInApp: true,
  };

  await sendSignInLinkToEmail(auth, email, actionCodeSettings);
  // Remember the email so we can finish sign-in when the link is opened in
  // the same browser.
  window.localStorage.setItem(EMAIL_STORAGE_KEY, email);
}

/**
 * If the current URL is a magic-link sign-in URL, complete the sign-in.
 * Returns true if a sign-in was attempted (whether successful or not).
 *
 * Call this once on app startup, e.g. from a useEffect on mount.
 */
export async function completeMagicLinkSignInIfPresent() {
  const href = window.location.href;
  if (!isSignInWithEmailLink(auth, href)) {
    return false;
  }

  let email = window.localStorage.getItem(EMAIL_STORAGE_KEY);

  if (!email) {
    // User opened the link on a different device than they requested it on.
    // Ask them to retype their email.
    email = window.prompt(
      "Please confirm the email address you used to request the sign-in link."
    );
    if (!email) {
      return false;
    }
  }

  try {
    await signInWithEmailLink(auth, email, href);
    window.localStorage.removeItem(EMAIL_STORAGE_KEY);

    // Clean the URL so a refresh doesn't try to re-consume the link.
    const cleanUrl = new URL(href);
    cleanUrl.search = "";
    cleanUrl.hash = "";
    window.history.replaceState({}, "", cleanUrl.toString());

    return true;
  } catch (err) {
    console.error("Magic link sign-in failed:", err);
    window.alert(
      "Sign-in link is invalid or expired. Please request a new one."
    );
    return false;
  }
}

export async function signOut() {
  await firebaseSignOut(auth);
}
